import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Link2, Globe, Wallet, MessageSquare, User as UserIcon,
  BookOpen, Copy, ExternalLink, Crown, Trophy, DollarSign, Users,
  Sparkles, ShieldCheck, TrendingUp, Award, LogOut, ChevronRight, Loader2,
} from "lucide-react";
import SeoHead from "../components/SeoHead";
import LandingPagesList from "../components/LandingPagesList";
import AmbassadorAttributionCard from "../components/AmbassadorAttributionCard";

const TIER_META = {
  advocate:   { name: "Advocate",           icon: Sparkles,    color: "text-primary" },
  ambassador: { name: "Ambassador",         icon: ShieldCheck, color: "text-secondary" },
  elite:      { name: "Elite Ambassador",   icon: Crown,       color: "text-secondary" },
  regional:   { name: "Regional Director",  icon: Trophy,      color: "text-secondary" },
};

const TABS = [
  { id: "overview",     label: "Overview",       icon: LayoutDashboard },
  { id: "link",         label: "Referral Link",  icon: Link2 },
  { id: "landing",      label: "Landing Pages",  icon: Globe },
  { id: "kit",          label: "Marketing Kit",  icon: BookOpen },
  { id: "wallet",       label: "Wallet + Xaman", icon: Wallet },
  { id: "feed",         label: "Feed",           icon: MessageSquare },
  { id: "profile",      label: "Profile",        icon: UserIcon },
];

/**
 * AmbassadorBackOffice — the single control surface for every ambassador.
 *
 * Sidebar-driven tab UX distinct from the main app shell (no top nav / no
 * feed sidebar) so ambassadors have a proper "back office" feel. Access
 * is granted to any authenticated user with an active `ambassadors` row
 * (role=AMBASSADOR, role=ADMIN, or role=PROVIDER with
 * ambassadorGrantedProvider=True).
 *
 * Data is loaded lazily per-tab where practical — the Overview tab pulls
 * /ambassador/me on mount, other tabs fetch as needed. All destructive
 * or auth-changing actions live in the top-right user chip.
 */
export default function AmbassadorBackOffice() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const { user, logout, loading } = useAuth();
  const activeTab = params.get("tab") || "overview";
  const setTab = (t) => setParams({ tab: t });

  const [me, setMe] = useState(null);
  const [meLoading, setMeLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/ambassador/me");
        if (!cancelled) setMe(data);
      } catch {
        // Non-ambassador — bounce to signup
        if (!cancelled) nav("/ambassador/signup");
      } finally {
        if (!cancelled) setMeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [nav]);

  if (loading || meLoading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-secondary" />
      </div>
    );
  }

  if (!me?.isAmbassador) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-6">
          <div className="label-mystic text-secondary">Ambassador Program</div>
          <h1 className="font-serif text-3xl">You&apos;re not yet an Ambassador.</h1>
          <Link
            to="/ambassador/signup"
            className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground px-6 py-3 rounded-sm uppercase tracking-[0.18em] text-xs font-semibold"
            data-testid="back-office-signup-cta"
          >
            Sign up now
          </Link>
        </div>
      </div>
    );
  }

  const TierIcon = TIER_META[me.tier]?.icon || Sparkles;

  return (
    <div className="min-h-screen bg-background text-foreground flex" data-testid="ambassador-back-office">
      <SeoHead title="Ambassador Back Office — Cauldron" description="Your ambassador command centre." />
      {/* ─── Sidebar ─── */}
      <aside className="w-60 border-r border-secondary/15 bg-background/95 backdrop-blur sticky top-0 h-screen flex flex-col">
        <div className="px-5 py-6 border-b border-secondary/15">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/logo.png" alt="Cauldron" className="w-7 h-7" onError={(e) => (e.target.style.display = "none")} />
            <span className="font-serif text-lg">cauldron</span>
          </Link>
          <div className="label-mystic text-secondary mt-3 text-[10px]">Ambassador Office</div>
        </div>
        <nav className="flex-1 py-4 space-y-0.5" data-testid="back-office-sidebar">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition ${
                  active
                    ? "bg-secondary/12 border-l-2 border-secondary text-secondary"
                    : "text-foreground/70 hover:text-foreground hover:bg-secondary/5 border-l-2 border-transparent"
                }`}
                data-testid={`sidebar-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" />
                <span>{t.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="px-5 py-4 border-t border-secondary/15 space-y-3">
          <div className="flex items-center gap-2">
            <TierIcon className={`w-4 h-4 ${TIER_META[me.tier]?.color}`} />
            <span className="text-xs text-foreground/70">{TIER_META[me.tier]?.name}</span>
            {me.isFounding && <Crown className="w-3 h-3 text-secondary" />}
          </div>
          <div className="text-[11px] text-foreground/55 truncate" title={user?.email}>{user?.email}</div>
          <button
            onClick={() => { logout(); nav("/"); }}
            className="w-full text-[11px] text-foreground/55 hover:text-secondary inline-flex items-center gap-1.5"
            data-testid="back-office-logout"
          >
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </aside>

      {/* ─── Main content ─── */}
      <main className="flex-1 min-w-0">
        <div className="max-w-5xl mx-auto px-8 py-10">
          {activeTab === "overview" && <OverviewTab me={me} />}
          {activeTab === "link"     && <LinkTab me={me} />}
          {activeTab === "landing"  && <LandingTab />}
          {activeTab === "kit"      && <KitTab />}
          {activeTab === "wallet"   && <WalletTab />}
          {activeTab === "feed"     && <FeedTab />}
          {activeTab === "profile"  && <ProfileTab me={me} user={user} />}
        </div>
      </main>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Overview — headline stats + 20-referral milestone bar
   ───────────────────────────────────────────────────────────── */
function OverviewTab({ me }) {
  const rateCents = me.settings?.commissionUsdCentsByTier?.[me.tier] || 500;
  const refs = me.totalPractitionerReferrals || 0;
  const slotsCap = me.founding20Cap || 20;
  const slotsTaken = me.founding20SlotsTaken || 0;
  const slotsLeft = Math.max(0, slotsCap - slotsTaken);
  const hasGrant = !!me.foundingPractitionerGranted;
  const myNumber = me.foundingPractitionerNumber;

  return (
    <div className="space-y-8" data-testid="tab-overview">
      <header>
        <div className="label-mystic text-secondary">Overview</div>
        <h1 className="font-serif text-4xl mt-2">Welcome back.</h1>
        <p className="text-foreground/60 text-sm mt-1">
          Recurring rate at your tier · <strong className="text-secondary">${(rateCents / 100).toFixed(2)}/mo</strong> per active practitioner subscription
        </p>
      </header>

      {/* Founding-20 Practitioner Grant */}
      <div className="parchment p-6 border-secondary/40" data-testid="grant-status-card">
        <div className="flex items-baseline justify-between mb-3 gap-4">
          <div className="min-w-0">
            <div className="label-mystic text-secondary">Founding-20 Practitioner Grant</div>
            <div className="text-xs text-foreground/60 mt-1">
              {hasGrant
                ? `✓ Founding Practitioner #${myNumber} — 1 free year of the Practitioner tier is active on your account.`
                : slotsLeft > 0
                  ? `The first 20 ambassadors receive 1 free year of Practitioner. ${slotsLeft} slot${slotsLeft === 1 ? "" : "s"} remaining.`
                  : "All 20 founding grants have been claimed."}
            </div>
          </div>
          <div className="font-serif text-3xl text-secondary shrink-0" data-testid="grant-slots-count">
            {hasGrant ? `#${myNumber}` : `${slotsTaken}/${slotsCap}`}
          </div>
        </div>
        <div className="h-3 bg-foreground/10 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-700 ${hasGrant ? "bg-secondary" : "bg-secondary/70"}`}
            style={{ width: `${Math.min(100, (slotsTaken / slotsCap) * 100)}%` }}
          />
        </div>
        {hasGrant && (
          <Link
            to="/profile/me"
            className="inline-flex items-center gap-2 mt-4 text-secondary hover:underline text-sm"
            data-testid="grant-view-profile"
          >
            View your Practitioner profile <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users}       label="Practitioners Recruited" value={refs} sub={`${me.totalSeekerReferrals || 0} seekers`} testid="stat-recruits" />
        <StatCard icon={DollarSign}  label="Last 30d USD" value={`$${(me.last30dUsd || 0).toFixed(2)}`} sub={`${me.last30dSubs || 0} subs`} testid="stat-30d" />
        <StatCard icon={TrendingUp}  label="Projected Monthly" value={`$${(me.projectedMonthlyUsd || 0).toFixed(2)}`} sub="recurring run-rate" testid="stat-projected" />
        <StatCard icon={Award}       label="Lifetime USD" value={`$${(me.totalCommissionUsd || 0).toFixed(2)}`} sub={`${me.totalCommissionCaul || 0} CAUL`} testid="stat-lifetime" />
      </div>

      {/* Recent commissions */}
      <div className="parchment p-6">
        <div className="label-mystic text-secondary mb-4">Recent Commissions</div>
        {(!me.recentCommissions || me.recentCommissions.length === 0) ? (
          <div className="text-sm text-foreground/60">Nothing yet — share your link to get started.</div>
        ) : (
          <div className="space-y-2">
            {me.recentCommissions.slice(0, 10).map((c) => (
              <div key={c.id} className="flex items-center justify-between border-b border-foreground/5 pb-2 last:border-0" data-testid={`comm-${c.id}`}>
                <div>
                  <div className="text-sm">{c.referredUserName || "Practitioner"}</div>
                  <div className="text-[11px] text-foreground/50">{c.type} · {new Date(c.createdAt).toLocaleDateString()}</div>
                </div>
                <div className="font-serif text-secondary">+${((c.amountUsdCents || 0) / 100).toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="parchment p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="label-mystic text-foreground/60">{label}</div>
        <Icon className="w-4 h-4 text-secondary" />
      </div>
      <div className="font-serif text-3xl">{value}</div>
      {sub && <div className="text-[11px] text-foreground/55 mt-1">{sub}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Referral Link
   ───────────────────────────────────────────────────────────── */
function LinkTab({ me }) {
  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied");
  };
  return (
    <div className="space-y-6" data-testid="tab-link">
      <header>
        <div className="label-mystic text-secondary">Referral Link</div>
        <h1 className="font-serif text-4xl mt-2">Your personal share URL.</h1>
        <p className="text-foreground/60 text-sm mt-1">Anyone who signs up through this link is attributed to you forever.</p>
      </header>
      <div className="parchment p-6">
        <div className="label-mystic text-foreground/70 mb-2">Main link</div>
        <div className="flex gap-2 items-center">
          <input
            readOnly value={me.referralUrl}
            className="flex-1 bg-input border border-secondary/25 rounded-sm p-3 font-mono text-sm"
            data-testid="link-main-input"
          />
          <button
            onClick={() => copy(me.referralUrl)}
            className="bg-secondary text-secondary-foreground hover:bg-secondary/85 px-4 py-3 rounded-sm inline-flex items-center gap-2"
            data-testid="link-main-copy"
          >
            <Copy className="w-4 h-4" /> Copy
          </button>
        </div>
        <div className="text-[11px] text-foreground/55 mt-3">
          Your referral code · <strong className="text-secondary">{me.referralCode}</strong>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Landing Pages
   ───────────────────────────────────────────────────────────── */
function LandingTab() {
  return (
    <div className="space-y-6" data-testid="tab-landing">
      <header>
        <div className="label-mystic text-secondary">Landing Pages</div>
        <h1 className="font-serif text-4xl mt-2">Route traffic by niche.</h1>
        <p className="text-foreground/60 text-sm mt-1">
          Every page below is pre-tagged with your referral code — home, seekers, 17 practitioner
          niches, the founding-elders directory, and the ambassador program. Share the URL that best
          matches the audience you&apos;re talking to and every signup rolls up to you.
        </p>
      </header>
      <AmbassadorAttributionCard days={30} />
      <LandingPagesList />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Marketing Kit — links out to the existing kit page for now
   ───────────────────────────────────────────────────────────── */
function KitTab() {
  return (
    <div className="space-y-6" data-testid="tab-kit">
      <header>
        <div className="label-mystic text-secondary">Marketing Kit</div>
        <h1 className="font-serif text-4xl mt-2">Scripts, graphics, and copy.</h1>
        <p className="text-foreground/60 text-sm mt-1">
          Every block has a <code className="text-secondary">{`{LINK}`}</code> placeholder — swap your referral URL from the previous tab.
        </p>
      </header>
      <div className="parchment p-6">
        <Link
          to="/ambassadors/kit"
          className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/85 px-6 py-3 rounded-sm uppercase tracking-[0.18em] text-xs font-semibold"
          data-testid="kit-open-full"
        >
          Open Full Marketing Kit <ExternalLink className="w-4 h-4" />
        </Link>
        <p className="text-[11px] text-foreground/55 mt-3">Social captions · Emails · DM/Text · Graphics + video clips coming soon.</p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Wallet — CAUL balance + Xaman link status
   ───────────────────────────────────────────────────────────── */
function WalletTab() {
  const [data, setData] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/ambassador/wallet");
      setData(data);
    } catch {
      toast.error("Failed to load wallet");
    }
  };
  useEffect(() => { load(); }, []);

  const connectXaman = async () => {
    setConnecting(true);
    try {
      const { data: signin } = await api.post("/caul-xrpl/xaman/signin-init");
      // Open the Xaman sign-in URL — user signs on their phone, then hits the confirm endpoint
      if (signin?.qrCode || signin?.deepLink || signin?.next?.always) {
        const url = signin?.next?.always || signin?.deepLink || signin?.qrCode;
        window.open(url, "_blank", "noopener");
        toast.success("Approve in Xaman, then click 'I signed'");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xaman not configured");
    } finally {
      setConnecting(false);
    }
  };

  const disconnectXaman = async () => {
    if (!window.confirm("Disconnect your Xaman wallet? Your $CAUL balance stays safe — you'll just need to reconnect (or connect a different wallet) to move funds on-chain.")) return;
    try {
      await api.post("/caul-xrpl/my-wallet/disconnect");
      toast.success("Xaman wallet disconnected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
  };

  if (!data) return <div className="text-sm text-foreground/60"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>;

  return (
    <div className="space-y-6" data-testid="tab-wallet">
      <header>
        <div className="label-mystic text-secondary">Wallet</div>
        <h1 className="font-serif text-4xl mt-2">$CAUL &amp; Xaman.</h1>
        <p className="text-foreground/60 text-sm mt-1">
          Earn 500 CAUL per paid practitioner referral. Connect Xaman once — it stays linked indefinitely until you disconnect.
        </p>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="parchment p-5" data-testid="wallet-caul">
          <div className="label-mystic text-foreground/60">Liquid CAUL</div>
          <div className="font-serif text-4xl text-secondary mt-3">{data.caul.balance.toLocaleString()}</div>
          <div className="text-[11px] text-foreground/55 mt-1">Locked: {data.caul.locked.toLocaleString()}</div>
        </div>
        <div className="parchment p-5" data-testid="wallet-commissions">
          <div className="label-mystic text-foreground/60">Commissions</div>
          <div className="font-serif text-4xl text-secondary mt-3">${data.commissions.totalUsd.toFixed(2)}</div>
          <div className="text-[11px] text-foreground/55 mt-1">{data.commissions.practitionerReferrals} practitioners · {data.commissions.totalCaul} CAUL bonuses</div>
        </div>
      </div>

      <div className="parchment p-6" data-testid="wallet-xaman">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div className="min-w-0">
            <div className="label-mystic text-secondary">Xaman Wallet</div>
            <div className="text-xs text-foreground/60 mt-1">
              {data.xaman.linked
                ? "Your wallet stays linked indefinitely. Only you can disconnect it."
                : "Connect a Xaman (XRPL) wallet to receive on-chain $CAUL cashouts."}
            </div>
          </div>
          {data.xaman.linked ? (
            <span className="inline-flex items-center gap-1.5 bg-secondary/15 border border-secondary/40 text-secondary px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-wider shrink-0" data-testid="xaman-linked-badge">
              <ShieldCheck className="w-3 h-3" /> Linked
            </span>
          ) : null}
        </div>
        {data.xaman.linked ? (
          <div className="space-y-3 text-sm">
            <div><span className="text-foreground/55">Address:</span> <span className="font-mono text-secondary break-all">{data.xaman.xrplAddress}</span></div>
            <div><span className="text-foreground/55">Trustline:</span> {data.xaman.trustlineActive ? "Active" : "Not yet"}</div>
            <div className="pt-2 border-t border-secondary/15 flex items-center gap-3">
              <button
                onClick={disconnectXaman}
                className="border border-destructive/45 text-destructive hover:bg-destructive/10 px-4 py-2 rounded-sm text-xs uppercase tracking-[0.16em]"
                data-testid="xaman-disconnect-btn"
              >
                Disconnect Wallet
              </button>
              <button
                onClick={connectXaman}
                disabled={connecting}
                className="border border-secondary/40 text-secondary hover:bg-secondary/10 px-4 py-2 rounded-sm text-xs uppercase tracking-[0.16em] inline-flex items-center gap-1.5"
                data-testid="xaman-reconnect-btn"
              >
                {connecting ? "Opening Xaman…" : "Switch Wallet"}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={connectXaman}
            disabled={connecting}
            className="bg-secondary text-secondary-foreground hover:bg-secondary/85 disabled:opacity-60 px-6 py-3 rounded-sm uppercase tracking-[0.18em] text-xs font-semibold inline-flex items-center gap-2"
            data-testid="xaman-connect-btn"
          >
            {connecting ? "Opening Xaman…" : "Connect Xaman Wallet"}
          </button>
        )}
      </div>

      {/* ─── Recent activity ─── */}
      <div className="parchment p-6" data-testid="wallet-activity">
        <div className="flex items-baseline justify-between mb-4">
          <div className="label-mystic text-secondary">Recent Activity</div>
          <div className="text-[11px] text-foreground/50">Last 10 events</div>
        </div>
        {(!data.activity || data.activity.length === 0) ? (
          <div className="text-sm text-foreground/55">
            Nothing yet. Every 100 CAUL bonus and $5–$24.99 commission
            will land here as you refer paid practitioners.
          </div>
        ) : (
          <ul className="divide-y divide-foreground/5">
            {data.activity.map((a) => (
              <li key={a.id} className="py-2.5 flex items-center gap-3" data-testid={`activity-${a.id}`}>
                <span className={`w-10 h-10 rounded-sm flex items-center justify-center text-[10px] font-semibold shrink-0 ${
                  a.type === "caul_bonus"
                    ? "bg-secondary/15 text-secondary border border-secondary/30"
                    : "bg-primary/15 text-primary border border-primary/30"
                }`}>
                  {a.type === "caul_bonus" ? "CAUL" : "USD"}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{a.note}</div>
                  <div className="text-[11px] text-foreground/50">
                    {a.createdAt ? new Date(a.createdAt).toLocaleDateString(undefined, {
                      month: "short", day: "numeric", year: "numeric",
                    }) : "—"}
                  </div>
                </div>
                <div className={`font-serif text-lg shrink-0 ${a.type === "caul_bonus" ? "text-secondary" : "text-primary"}`}>
                  +{a.type === "caul_bonus" ? a.amount.toLocaleString() : `$${a.amount.toFixed(2)}`}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Feed — inline composer + last 10 posts inside the back office
   ───────────────────────────────────────────────────────────── */
function FeedTab() {
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [posts, setPosts] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/posts");
      setPosts(Array.isArray(data) ? data.slice(0, 10) : []);
    } catch {
      setPosts([]);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const text = content.trim();
    if (!text) return;
    setPosting(true);
    try {
      await api.post("/posts", { content: text, type: "TEXT" });
      setContent("");
      toast.success("Posted to the feed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Post failed");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="tab-feed">
      <header>
        <div className="label-mystic text-secondary">Feed</div>
        <h1 className="font-serif text-4xl mt-2">The Cauldron Feed.</h1>
        <p className="text-foreground/60 text-sm mt-1">
          Post directly from your back office. Your voice reaches every seeker + practitioner.
        </p>
      </header>

      {/* Inline composer */}
      <form onSubmit={submit} className="parchment p-5 space-y-3" data-testid="feed-composer">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Share a ritual insight, a share-worthy invitation, a moment of the day…"
          className="w-full bg-input border border-secondary/25 rounded-sm p-3 text-sm resize-none focus:outline-none focus:border-secondary/60"
          data-testid="feed-composer-input"
        />
        <div className="flex items-center justify-between">
          <div className="text-[11px] text-foreground/50">{content.length}/2000</div>
          <button
            type="submit"
            disabled={posting || !content.trim()}
            className="bg-secondary text-secondary-foreground hover:bg-secondary/85 disabled:opacity-50 px-5 py-2 rounded-sm uppercase tracking-[0.16em] text-xs font-semibold inline-flex items-center gap-2"
            data-testid="feed-composer-submit"
          >
            {posting ? "Posting…" : "Post to Feed"}
          </button>
        </div>
      </form>

      {/* Recent posts (mini feed) */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <div className="label-mystic text-secondary">Recent Posts</div>
          <Link to="/feed" className="text-[11px] text-foreground/55 hover:text-secondary uppercase tracking-wider inline-flex items-center gap-1">
            Open full feed <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        {posts === null ? (
          <div className="text-sm text-foreground/55"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>
        ) : posts.length === 0 ? (
          <div className="parchment p-6 text-sm text-foreground/55 text-center">
            The cauldron is still. Be the first to speak.
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.id} className="parchment p-4" data-testid={`recent-post-${p.id}`}>
                <div className="flex items-center gap-2 text-[11px] text-foreground/55 mb-2">
                  <span className="uppercase tracking-wider text-secondary">
                    {p.provider?.businessName || p.user?.name || "Cauldron Member"}
                  </span>
                  <span>·</span>
                  <span>{p.createdAt ? new Date(p.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}</span>
                </div>
                {p.content && (
                  <p className="text-sm text-foreground/85 leading-relaxed line-clamp-3">{p.content}</p>
                )}
                {p.mediaUrl && p.type === "IMAGE" && (
                  <img src={p.mediaUrl} alt="" className="mt-3 max-h-40 rounded-sm object-cover" onError={(e) => (e.target.style.display = "none")} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Profile — public identity summary
   ───────────────────────────────────────────────────────────── */
function ProfileTab({ me, user }) {
  return (
    <div className="space-y-6" data-testid="tab-profile">
      <header>
        <div className="label-mystic text-secondary">Profile</div>
        <h1 className="font-serif text-4xl mt-2">{user?.name}</h1>
        <p className="text-foreground/60 text-sm mt-1">
          {me.isFounding ? `Founding Ambassador #${me.foundingNumber}` : `${TIER_META[me.tier]?.name || "Ambassador"}`}
          {" · joined "}{new Date(me.joinedAt).toLocaleDateString()}
        </p>
      </header>

      <div className="parchment p-6 space-y-3 text-sm">
        <div><span className="text-foreground/55">Email:</span> {user?.email}</div>
        <div><span className="text-foreground/55">Referral code:</span> <strong className="text-secondary">{me.referralCode}</strong></div>
        <div><span className="text-foreground/55">Referral URL:</span> <span className="font-mono text-secondary text-xs break-all">{me.referralUrl}</span></div>
        <div><span className="text-foreground/55">Tier:</span> {TIER_META[me.tier]?.name}</div>
        <div><span className="text-foreground/55">Practitioner referrals:</span> {me.totalPractitionerReferrals}</div>
      </div>

      <div className="parchment p-6">
        <div className="label-mystic text-secondary mb-3">Public Profile</div>
        <p className="text-sm text-foreground/70">
          Your public seeker profile is visible at:
        </p>
        <Link
          to={`/profile/${user?.id}`}
          className="inline-flex items-center gap-2 mt-3 text-secondary hover:underline text-sm"
          data-testid="profile-view-public"
        >
          View public profile <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
